import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Icon,
  Chip,
  Skeleton,
  useTheme,
  alpha,
} from "@mui/material";
import { motion } from "framer-motion";

interface StatCardProps {
  title: string;
  value: string | number;
  icon: string;
  color: string;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  isLoading?: boolean;
  delay?: number;
  subtitle?: string;
  action?: React.ReactNode;
}

export const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  icon,
  color,
  trend,
  isLoading = false,
  delay = 0,
  subtitle,
  action,
}) => {
  const theme = useTheme();

  // Custom trend colors
  const trendColors = {
    positive: {
      main: "#0891B2", // Teal color
      light: alpha("#0891B2", 0.1),
      contrastText: "#fff",
    },
    negative: {
      main: "#E57373", // Light red color
      light: alpha("#E57373", 0.1),
      contrastText: "#fff",
    },
  };

  return (
    <Card
      component={motion.div}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay }}
      sx={{
        height: "100%",
        position: "relative",
        overflow: "visible",
        background: `linear-gradient(145deg, ${alpha(
          theme.palette.background.paper,
          0.9
        )}, ${alpha(theme.palette.background.paper, 0.95)})`,
        backdropFilter: "blur(8px)",
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        boxShadow: `0 4px 24px ${alpha(theme.palette.common.black, 0.04)}`,
        borderRadius: "16px",
      }}
    >
      <CardContent
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          position: "relative",
          pt: { xs: 3, sm: 4, md: 5 },
          pb: { xs: 2, sm: 2.5, md: 3 },
          px: { xs: 2, sm: 3 },
        }}
      >
        {isLoading ? (
          <>
            <Skeleton variant="text" width="60%" height={24} />
            <Skeleton variant="text" width="40%" height={40} />
          </>
        ) : (
          <>
            {/* <Box
              sx={{
                position: 'absolute',
                top: -20,
                bgcolor: `${color}15`,
                p: 1.5,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: `1px solid ${alpha(color, 0.1)}`,
                boxShadow: `0 4px 12px ${alpha(color, 0.1)}`,
              }}
            >
              <Icon style={{ color, fontSize: 24 }}>{icon}</Icon>
            </Box> */}

            <Typography
              variant="h3"
              sx={{
                mb: { xs: 0.5, sm: 1 },
                fontWeight: 600,
                color: theme.palette.text.primary,
                fontSize: {
                  xs: "1.5rem",
                  sm: "1.75rem",
                  md: "2rem",
                },
                lineHeight: 1.2,
                wordBreak: "break-word",
                maxWidth: "100%",
                whiteSpace: "pre-wrap",
              }}
            >
              {value}
            </Typography>

            <Typography
              variant="body1"
              color="text.secondary"
              sx={{
                mb: { xs: 0.5, sm: 1 },
                fontWeight: 500,
                fontSize: {
                  xs: "0.875rem",
                  sm: "1rem",
                },
                maxWidth: "100%",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {title}
            </Typography>

            {subtitle && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  mb: { xs: 0.5, sm: 1 },
                  fontSize: {
                    xs: "0.75rem",
                    sm: "0.8125rem",
                  },
                  maxWidth: "100%",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {subtitle}
              </Typography>
            )}

            {trend && (
              <Chip
                size="small"
                icon={
                  <Icon
                    sx={{
                      color: "#fff",
                      fontSize: {
                        xs: "16px !important",
                        sm: "18px !important",
                        md: "20px !important",
                      },
                      "&.MuiChip-icon": {
                        color: "#fff !important",
                      },
                    }}
                  >
                    {trend.isPositive ? "trending_up" : "trending_down"}
                  </Icon>
                }
                label={`${trend.isPositive ? "+" : ""}${trend.value}%`}
                sx={{
                  height: { xs: 20, sm: 22, md: 24 },
                  mt: { xs: 0.5, sm: 1 },
                  backgroundColor: trend.isPositive
                    ? trendColors.positive.main
                    : trendColors.negative.main,
                  color: "#fff",
                  "& .MuiChip-icon": {
                    color: "#fff !important",
                  },
                  "& .MuiChip-label": {
                    px: { xs: 0.5, sm: 0.75, md: 1 },
                    fontSize: {
                      xs: "0.75rem",
                      sm: "0.8rem",
                      md: "0.85rem",
                    },
                    fontWeight: 500,
                    color: "#fff",
                  },
                  "& .MuiChip-iconSmall": {
                    mr: { xs: 0.25, sm: 0.5 },
                    color: "#fff !important",
                  },
                }}
              />
            )}

            {action && <Box sx={{ mt: { xs: 1, sm: 2 } }}>{action}</Box>}
          </>
        )}
      </CardContent>
    </Card>
  );
};
